import { FC, useRef, useCallback, ComponentProps, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { getSrcSetString } from './helpers/getSrcSetString';

import { ViewInRoomImage_item$key } from './__generated__/ViewInRoomImage_item.graphql';
import { ViewInRoomImage_viewInRoomPhoto$key } from './__generated__/ViewInRoomImage_viewInRoomPhoto.graphql';

type OnVisibilityChange = ComponentProps<typeof VisibilityTracker>['onVisibilityChange'];

const IMAGE_SIZE = 1200;

const loadImage = ({
    src,
    srcset,
    sizes,
}: {
    src: string;
    srcset?: string;
    sizes?: string;
}): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve(img);
        };
        img.onerror = () => {
            reject();
        };

        // Order matters here. Need to set sizes before srcset to avoid multiple calls of the image in safari
        if (sizes) {
            img.sizes = sizes;
        }
        if (srcset) {
            img.srcset = srcset;
        }

        img.src = src;
    });

const getSrcSetAttrs = ({
    src,
    srcSetSizes,
    srcSetWidths,
    srcSetQuality,
}: {
    src: string;
    srcSetSizes?: string;
    srcSetWidths?: number[];
    srcSetQuality?: number;
}): { srcset: string; sizes: string } | ObjectType => {
    if (!srcSetSizes) {
        return {};
    }

    return {
        srcset: getSrcSetString(src, {
            widths: srcSetWidths,
            quality: srcSetQuality,
        }),
        sizes: srcSetSizes,
    };
};

const itemFragment = graphql`
    fragment ViewInRoomImage_item on Item {
        measurement {
            convertedMeasurements {
                width
            }
        }
    }
`;

const viewInRoomPhotoFragment = graphql`
    fragment ViewInRoomImage_viewInRoomPhoto on viewInRoomPhotos {
        src
        centerPosition {
            top
            left
        }
        comparatorWidth {
            pixel
            inches
        }
    }
`;

export const ViewInRoomImage: FC<{
    viewInRoomPhoto: ViewInRoomImage_viewInRoomPhoto$key;
    item: ViewInRoomImage_item$key;
    className: string;
    mainImagePaths: {
        smallPath?: string | null;
        masterOrZoomPath?: string | null;
    };
    useEagerImageLoad: boolean;

    srcSetSizes?: string;
    srcSetWidths?: Array<number>;
    srcSetQuality?: number;
    imageLoadVerticalOffset?: number;
    onContentLoaded?: () => void;
}> = ({
    className,
    mainImagePaths,
    useEagerImageLoad,

    srcSetSizes,
    srcSetWidths,
    srcSetQuality,
    imageLoadVerticalOffset = 100,
    onContentLoaded = () => {},

    ...props
}) => {
    const isConstructTriggered = useRef(false);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const item = useFragment(itemFragment, props.item);
    const viewInRoomPhoto = useFragment(viewInRoomPhotoFragment, props.viewInRoomPhoto);
    const itemWidth = item?.measurement?.convertedMeasurements?.width;

    const constructCanvas = useCallback((): void => {
        const canvas = canvasRef.current;
        const ctx = canvas?.getContext('2d');
        const { src } = viewInRoomPhoto;
        const { smallPath, masterOrZoomPath } = mainImagePaths;
        const mainImgSrc = smallPath || masterOrZoomPath;

        if (
            !canvas ||
            !ctx ||
            !src ||
            !mainImgSrc ||
            !masterOrZoomPath ||
            !itemWidth ||
            isConstructTriggered.current
        ) {
            return;
        }

        isConstructTriggered.current = true;

        (async () => {
            try {
                const [roomImg, artworkImg] = await Promise.all([
                    loadImage({
                        src,
                        ...getSrcSetAttrs({ src, srcSetSizes, srcSetWidths, srcSetQuality }),
                    }),
                    loadImage({
                        src: mainImgSrc,
                        ...getSrcSetAttrs({
                            src: masterOrZoomPath,
                            srcSetSizes,
                            srcSetWidths,
                            srcSetQuality,
                        }),
                    }),
                ]);

                canvas.width = roomImg.width;
                canvas.height = roomImg.height;
                ctx.drawImage(roomImg, 0, 0);

                const proportion = IMAGE_SIZE / roomImg.width;
                const { comparatorWidth, centerPosition } = viewInRoomPhoto;

                const newWidth =
                    ((Number(comparatorWidth?.pixel) / Number(comparatorWidth?.inches)) *
                        Number(itemWidth)) /
                    proportion;
                //recalculate height based on aspect ration
                const newHeight = newWidth * (artworkImg.height / artworkImg.width);

                const x = Number(centerPosition?.left) / proportion - newWidth / 2;
                const y = Number(centerPosition?.top) / proportion - newHeight / 2;

                ctx.drawImage(artworkImg, x, y, newWidth, newHeight);

                onContentLoaded();
            } catch (e) {
                //something wrong
            }
        })();
    }, [
        viewInRoomPhoto,
        srcSetSizes,
        srcSetWidths,
        srcSetQuality,
        itemWidth,
        mainImagePaths,
        onContentLoaded,
    ]);

    useEffect(() => {
        if (useEagerImageLoad) {
            constructCanvas();
        }
    }, [useEagerImageLoad, constructCanvas]);

    const onVisibilityChange: OnVisibilityChange = useCallback(
        ({ isVisible }) => {
            if (isVisible) {
                constructCanvas();
            }
        },
        [constructCanvas]
    );

    return (
        <>
            <canvas ref={canvasRef} className={className} />
            {!useEagerImageLoad && (
                <VisibilityTracker
                    elementRef={canvasRef}
                    onVisibilityChange={onVisibilityChange}
                    observerOptions={{
                        rootMargin: `${imageLoadVerticalOffset}px 0px ${imageLoadVerticalOffset}px 0px`,
                    }}
                />
            )}
        </>
    );
};
